import { useEffect, useState } from "react";
import { BarChart3, Crosshair, FileDown, LayoutDashboard, Play, Plus, Repeat, Search, Settings, Target } from "lucide-react";
import { useRouter } from "@shared/client/navigation";
import { useView } from "@shared/client/view";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@shared/components/ui/command";
import { Button } from "@shared/components/ui/button";
import { normalizeNamedContacts, type Lead } from "@shared/lib/leads-shared";
import type { OfferSummary } from "@shared/lib/offers-shared";
import { relativeTime } from "@shared/lib/relative-time";
import { downloadLeadsExport } from "@/components/leads-export-button";
import { OrgLogo } from "@shared/components/org-logo";
import { useLocale, useT } from "@shared/client/locale";
import type { Messages } from "@shared/i18n";

type LeadsData = { leads: Lead[] };
type ReportsData = { runs: Array<{ id: string; status: string; created_at: string; product_url: string; reports: unknown }> };

const PAGES: Array<{ href: string; label: keyof Messages["shell"]["nav"]; icon: typeof LayoutDashboard }> = [
  { href: "/app", label: "overview", icon: LayoutDashboard },
  { href: "/app/leads", label: "leads", icon: Target },
  { href: "/app/reports", label: "reports", icon: BarChart3 },
  { href: "/app/offer", label: "offers", icon: Crosshair },
  { href: "/app/settings", label: "settings", icon: Settings },
];

/** Sans recherche, la palette ne liste que les leads les plus récents. */
const IDLE_LEADS = 5;

function offerNameOf(reports: unknown) {
  const report = Array.isArray(reports) ? reports[0] : reports;
  return report && typeof report === "object" && "offer_name" in report && typeof report.offer_name === "string"
    ? report.offer_name
    : null;
}

/**
 * ⌘K (Ctrl K) : aller à un lead, un interlocuteur, un passage ou un écran, et
 * lancer un passage, changer d'offre ou exporter les leads, sans quitter le
 * clavier. Les données sont celles des écrans (mêmes vues, même cache),
 * chargées à l'ouverture.
 * Dans une fiche lead ouverte, ⌘K reste le menu d'actions de la fiche.
 */
export function CommandPalette({ offers, activeOfferId }: { offers: OfferSummary[]; activeOfferId: string | null }) {
  const router = useRouter();
  const t = useT();
  const locale = useLocale();
  const copy = t.shell.palette;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [shortcut] = useState(() =>
    typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent) ? "⌘K" : "Ctrl K",
  );
  const leads = useView<LeadsData>(open ? "/api/views/leads" : null).data?.leads ?? [];
  const runs = useView<ReportsData>(open ? "/api/views/reports" : null).data?.runs ?? [];
  const [now] = useState(() => Date.now());
  const [exporting, setExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState("");

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "k") return;
      // Une fenêtre déjà ouverte (fiche lead) garde son propre ⌘K ; une bulle
      // de la visite guidée n'en est pas une.
      if (!open && document.querySelector("[role='dialog']:not([data-tour-bubble])")) return;
      event.preventDefault();
      setExportMessage("");
      setOpen((current) => !current);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  function go(href: string) {
    setOpen(false);
    setSearch("");
    router.push(href);
  }

  /** Le fichier se télécharge sans quitter la page ; un échec reste affiché dans la palette. */
  async function exportLeads() {
    setExporting(true);
    setExportMessage("");
    try {
      const notice = await downloadLeadsExport({ filter: "all" });
      if (notice) {
        setExportMessage(notice);
        return;
      }
      setOpen(false);
      setSearch("");
    } catch (cause) {
      setExportMessage(cause instanceof Error ? cause.message : copy.exportFailed);
    } finally {
      setExporting(false);
    }
  }

  async function selectOffer(offerId: string) {
    setOpen(false);
    await fetch("/api/offers/select", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ offerId }),
    }).catch(() => {});
    router.refresh();
  }

  const searching = search.trim().length > 0;
  const shownLeads = searching ? leads.filter((lead) => !lead.archived_at) : leads.filter((lead) => !lead.archived_at).slice(0, IDLE_LEADS);
  const contacts = searching
    ? leads.flatMap((lead) => normalizeNamedContacts(lead.contacts).map((contact) => ({ lead, contact })))
    : [];
  const completedRuns = runs.filter((run) => run.status === "completed").slice(0, searching ? 20 : 3);
  const otherOffers = offers.filter((offer) => offer.id !== activeOfferId && !offer.archivedAt);

  return (
    <>
      <Button
        aria-label={copy.search}
        className="h-[38px] gap-2 px-3 text-[12.5px] font-normal text-muted-foreground max-md:w-[38px] max-md:px-0"
        onClick={() => setOpen(true)}
        type="button"
        variant="outline"
      >
        <Search aria-hidden="true" />
        <span className="max-md:hidden">{copy.search}</span>
        <kbd className="rounded border bg-muted px-1.5 font-sans text-[12px] max-md:hidden">{shortcut}</kbd>
      </Button>
      <CommandDialog
        className="sm:max-w-[560px]"
        description={copy.description}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            setSearch("");
            setExportMessage("");
          }
        }}
        open={open}
        showCloseButton={false}
        title={copy.title}
      >
        <CommandInput onValueChange={setSearch} placeholder={copy.placeholder} value={search} />
        <CommandList className="max-h-[420px]">
          <CommandEmpty>{copy.empty}</CommandEmpty>
          {shownLeads.length > 0 && (
            <CommandGroup heading={searching ? copy.leads : copy.recentLeads}>
              {shownLeads.map((lead) => (
                <CommandItem
                  key={lead.id}
                  keywords={lead.category ? [lead.category] : undefined}
                  onSelect={() => go(`/app/leads?lead=${lead.id}`)}
                  value={lead.name}
                >
                  <OrgLogo name={lead.name} size={22} website={lead.website} />
                  <span className="min-w-0 flex-1 truncate">
                    <span className="font-medium">{lead.name}</span>
                    <span className="text-muted-foreground">
                      {" · "}
                      {t.shell.leadStatuses[lead.status]}
                      {lead.latest_signal?.market_clue ? ` · ${lead.latest_signal.market_clue}` : ""}
                    </span>
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {contacts.length > 0 && (
            <CommandGroup heading={copy.contacts}>
              {contacts.map(({ lead, contact }) => (
                <CommandItem
                  key={contact.id}
                  keywords={[contact.contact_role ?? ""]}
                  onSelect={() => go(`/app/leads?lead=${lead.id}`)}
                  value={`${contact.contact_name} — ${lead.name}`}
                >
                  <span className="grid size-[22px] flex-none place-items-center rounded-full bg-secondary text-[10px] font-medium">
                    {contact.contact_name.split(/\s+/).slice(0, 2).map((word) => word[0]?.toUpperCase()).join("")}
                  </span>
                  <span className="min-w-0 flex-1 truncate">
                    <span className="font-medium">{contact.contact_name}</span>
                    <span className="text-muted-foreground"> · {[contact.contact_role, lead.name].filter(Boolean).join(", ")}</span>
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {completedRuns.length > 0 && (
            <CommandGroup heading={copy.passes}>
              {completedRuns.map((run) => (
                <CommandItem
                  key={run.id}
                  onSelect={() => go(`/app/reports/${run.id}`)}
                  value={`${copy.passReport} ${relativeTime(Date.parse(run.created_at), now, false, locale)} ${run.id.slice(0, 8)}`}
                  keywords={[offerNameOf(run.reports) ?? run.product_url]}
                >
                  <BarChart3 aria-hidden="true" />
                  <span className="min-w-0 flex-1 truncate">
                    {copy.passReport} <span className="text-muted-foreground">· {relativeTime(Date.parse(run.created_at), now, false, locale)}</span>
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          <CommandGroup heading={copy.actions}>
            <CommandItem onSelect={() => go("/app#watch")} keywords={copy.launchKeywords.split(" ")} value={copy.launchPass}>
              <Play aria-hidden="true" /> {copy.launchPass}
            </CommandItem>
            {otherOffers.map((offer) => (
              <CommandItem
                key={offer.id}
                onSelect={() => void selectOffer(offer.id)}
                keywords={[copy.offerKeyword]} value={copy.switchOffer(offer.name)}
              >
                <Repeat aria-hidden="true" /> {copy.switchOffer(offer.name)}
              </CommandItem>
            ))}
            <CommandItem onSelect={() => go("/app/offer")} value={copy.addOffer}>
              <Plus aria-hidden="true" /> {copy.addOffer}
            </CommandItem>
            {leads.some((lead) => !lead.archived_at) && (
              <CommandItem
                disabled={exporting}
                keywords={copy.exportKeywords.split(" ")}
                onSelect={() => void exportLeads()}
                value={copy.exportLeads}
              >
                <FileDown aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  {copy.exportLeads}
                  {exportMessage && (
                    <span className="block text-[12px] text-destructive" role="alert">
                      {exportMessage}
                    </span>
                  )}
                </span>
              </CommandItem>
            )}
          </CommandGroup>
          <CommandGroup heading={copy.goTo}>
            {PAGES.map(({ href, label, icon: Icon }) => (
              <CommandItem key={href} onSelect={() => go(href)} value={copy.goToPage(t.shell.nav[label])}>
                <Icon aria-hidden="true" /> {t.shell.nav[label]}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}
